
import { defineComponent, SetupContext, ref, ComponentPublicInstance, watch, toRefs, onMounted } from "vue";
import NativeClient from "@/client/native-client";

export default defineComponent({
  name: "HorizontalPaging",
  props: {
    /** selectIndex */
    currentPageIndex: {
      type: Number,
      required: true
    },
    /** ページの初期値 */
    initialPageIndex: {
      type: Number,
      default: 0
    }
  },
  setup(props, context: SetupContext) {
    const { currentPageIndex } = toRefs(props);
    const innerCurrentPageIndex = ref(currentPageIndex.value);
    const parentContent = ref<ComponentPublicInstance<HTMLElement>>();

    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const scrollAnimation = (currentPosition: number, targetPosition: number) => {
      let delta = 0;
      // アニメーション中は操作できないようにpointer-eventsを無効化
      scroll.value!.style.pointerEvents = "none";
      if (currentPosition == targetPosition) {
        // アニメーション終了なのでpointer-eventsを有効化
        scroll.value!.style.pointerEvents = "auto";
        NativeClient.sendDebugLog("HorizontalPaging scrollAnimation end currentPosition = " + currentPosition + ", targetPosition = " + targetPosition);
        return;
      } else if (currentPosition < targetPosition) {
        delta = 50;
      } else {
        delta = -50;
      }
      NativeClient.sendDebugLog("HorizontalPaging scrollAnimation currentPosition = " + currentPosition + ", targetPosition = " + targetPosition);
      let currentTargetPosition = currentPosition + delta;
      if (delta > 0 && targetPosition < currentTargetPosition) {
        currentTargetPosition = targetPosition;
      } else if (delta < 0 && currentTargetPosition < targetPosition) {
        currentTargetPosition = targetPosition;
      }
      scroll.value!.scrollTo({ top: 0, left: currentTargetPosition });

      window.setTimeout(() => {
        // スクロール動作停止解除
        scrollAnimation(currentTargetPosition, targetPosition);
      }, 20);
    };
    const setScrollTo = (position: number, isSmooth: boolean) => {
      NativeClient.sendDebugLog("HorizontalPaging setScrollTo position = " + position + ", isSmooth = " + isSmooth);
      if (scroll.value != undefined) {
        const itemWidth = window.innerWidth;
        NativeClient.sendDebugLog("HorizontalPaging setScrollTo current Scroll left = " + scroll.value!.scrollLeft);
        if (isSmooth) {
          const targetLeft = position * itemWidth;
          const currentScrollLeft = scroll.value!.scrollLeft;
          scrollAnimation(currentScrollLeft, targetLeft)
        } else {
          scroll.value!.scrollTo({ top: 0, left: itemWidth * position });
        }
      }
    };

    // 外部からのページ変更検知
    let isInitialCompleted = false;
    watch(currentPageIndex, () => {
      NativeClient.sendDebugLog("HorizontalPaging watch currentPageIndex = " + currentPageIndex.value);
      if (!isInitialCompleted) {
        NativeClient.sendDebugLog("HorizontalPaging watch initial not completed");
        return;
      }
      if (currentPageIndex.value != innerCurrentPageIndex.value) {
        setScrollTo(currentPageIndex.value, true);
        innerCurrentPageIndex.value = currentPageIndex.value;
      }
    });

    const { initialPageIndex } = toRefs(props);
    watch(initialPageIndex, () => {
      NativeClient.sendDebugLog("HorizontalPaging watch initialPageIndex");
      setScrollTo(initialPageIndex.value, false);
      innerCurrentPageIndex.value = initialPageIndex.value;
      isInitialCompleted = true;
    });

    // スクロール制御。デフォルトのスクロール動作（慣性スクロール）を抑止して、コードによるスクロールを行う
    let startX = 0;
    let moveX = 0;
    let lastX = 0;
    let startCurrentScrollLeft = 0;
    const touchStart = (e: TouchEvent) => {
      startX = e.touches[0].pageX;

      NativeClient.sendDebugLog("HorizontalPaging touchStart startX =" + startX );
      if (parentContent.value != undefined) {
        startCurrentScrollLeft = scroll.value!.scrollLeft;
        NativeClient.sendDebugLog("HorizontalPaging touchStart scrollPosition = " + scroll.value?.scrollLeft);
      }
    };
    const touchMove = (e: TouchEvent) => {
      moveX = e.touches[0].pageX - startX;
      lastX = e.touches[0].pageX ;
      const leftPosition = startCurrentScrollLeft - moveX;
      const screenWidth = window.innerWidth
      if (scroll.value != undefined && leftPosition > 0 && Math.abs(moveX) < screenWidth) {
        scroll.value!.scrollTo({ top: 0, left: leftPosition });
      }
      NativeClient.sendDebugLog("HorizontalPaging touchMove moveX = " + moveX + ", lastX = " + lastX);
    };
    const touchEnd = (e: TouchEvent) => {
      NativeClient.sendDebugLog("HorizontalPaging touchEnd");
      if (scroll.value != undefined && parentContent.value != undefined) {
        // スクロール領域の情報取得
        const scrollRect = scroll.value!.getBoundingClientRect();
        const scrollHorizontalCenter = (scrollRect.width - scrollRect.left) / 2;

        // スクロール内部の要素の取得
        const elements = parentContent.value!.children;
        const items = Array.from(elements);
        let position = 0;
        for (let index = 0; index < items.length; index ++) {
          const clientRect = items[index].getBoundingClientRect();
          NativeClient.sendDebugLog("HorizontalPaging touchEnd element[" + index + "] left = " + clientRect.left + ". width = " + clientRect.width);
          if (clientRect.left < scrollHorizontalCenter && scrollHorizontalCenter <= clientRect.right) {
            NativeClient.sendDebugLog("HorizontalPaging touchEnd hit");
            position = index;
            break;
          }
        }
        setScrollTo(position, true);
        innerCurrentPageIndex.value = position;
        context.emit("on-select", position);
      }
    };

    // 初期
    onMounted(() => {
      NativeClient.sendDebugLog("HorizontalPaging onMounted");
      if (currentPageIndex.value != 0) {
        setScrollTo(currentPageIndex.value, false);
        isInitialCompleted = true;
      }
    });

    return {
      scroll,
      touchStart,
      touchMove,
      touchEnd,
      parentContent
    };
  }
});
